import { useCallback, useRef, useState } from "react";
import type { ClipboardEvent, KeyboardEvent } from "react";

const OTP_LENGTH = 6;
const createEmptyOtp = () => Array(OTP_LENGTH).fill("");

export function useOtpCode() {
  const [otpValues, setOtpValues] = useState<string[]>(createEmptyOtp);
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const resetOtp = useCallback(() => {
    setOtpValues(createEmptyOtp());
  }, []);

  const focusFirstOtpInput = useCallback(() => {
    inputsRef.current[0]?.focus();
  }, []);

  const focusOtpInput = useCallback((index: number) => {
    inputsRef.current[Math.min(index, OTP_LENGTH - 1)]?.focus();
  }, []);

  const handleOtpChange = useCallback(
    (value: string, index: number) => {
      const digits = value.replace(/\D/g, "").split("");
      if (digits.length > 1) {
        const nextOtp = [...otpValues];
        digits.slice(0, OTP_LENGTH - index).forEach((digit, offset) => {
          nextOtp[index + offset] = digit;
        });
        setOtpValues(nextOtp);
        focusOtpInput(index + digits.length);
        return;
      }

      if (!/^[0-9]?$/.test(value)) return;

      const nextOtp = [...otpValues];
      nextOtp[index] = value;
      setOtpValues(nextOtp);

      if (value && index < OTP_LENGTH - 1) {
        focusOtpInput(index + 1);
      }
    },
    [focusOtpInput, otpValues]
  );

  const handleOtpKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>, index: number) => {
      if (event.key === "Backspace" && !otpValues[index] && index > 0) {
        focusOtpInput(index - 1);
      }
    },
    [focusOtpInput, otpValues]
  );

  const handleOtpPaste = useCallback(
    (event: ClipboardEvent<HTMLInputElement>, index: number) => {
      const digits = event.clipboardData.getData("text").replace(/\D/g, "").split("");
      if (digits.length === 0) return;

      event.preventDefault();

      const nextOtp = [...otpValues];
      digits.slice(0, OTP_LENGTH - index).forEach((digit, offset) => {
        nextOtp[index + offset] = digit;
      });

      setOtpValues(nextOtp);
      focusOtpInput(index + digits.length);
    },
    [focusOtpInput, otpValues]
  );

  const otp = otpValues.join("");

  return {
    otp,
    otpValues,
    inputsRef,
    isOtpComplete: otp.length === OTP_LENGTH,
    resetOtp,
    focusFirstOtpInput,
    handleOtpChange,
    handleOtpKeyDown,
    handleOtpPaste,
  };
}
